import React, { useState } from 'react';
import { Modal, Typography, Button, useToast, Badge, Alert, AlertDescription } from '@crisellevaldez/speculo-ui';
import { LoadingDialog } from '../../common/dialogs/LoadingDialog';
import { FileText, Calendar, Tag, Users, MapPin, Check, X } from 'lucide-react';
import { IconPill } from '../../common/IconPill';
import { createFlowExecutions } from '../../../api/flows';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { capitalizeFirst } from '../../../utils/strings';
import { formatDate } from '../../../utils/dateTime';
import { useReviveEngageWebhook } from '../../../hooks/flows/useReviveEngageWebhook';
import { Flow as FlowData } from '../../../hooks/useFlowsData';
import { useMLSSubscriptionStatus } from '../../../hooks/client/mls/useMLSSubscriptionStatus';

interface Flow {
  id: string;
  name: string;
  scheduling_window?: number;
  schedule_over_unit?: string;
  allowed_days: string[];
  allowed_hours?: {
    start_hour: number;
    end_hour: number;
  };
  exclude_holidays: boolean;
  transfer_numbers: Record<string, string>;
  scheduled_for?: string;
  description?: string;
  sub_type?: string;
  flow_type: string;
}

interface ScheduleFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  flowId: string;
  flowName: string;
  contactCount: number;
  selectedTags: string[];
  flow: Flow;
}

export const ScheduleFlowModal: React.FC<ScheduleFlowModalProps> = ({
  isOpen,
  onClose,
  flowId,
  flowName,
  contactCount,
  selectedTags,
  flow,
}) => {
  const [isScheduling, setIsScheduling] = useState(false);
  const { showToast } = useToast();
  const { organization } = useOrganizationStore();
  const timezone = organization?.timezone || 'UTC';
  const transferLines = flow.transfer_numbers ? Object.keys(flow.transfer_numbers) : [];
  const { sendWebhook: sendReviveEngageWebhook } = useReviveEngageWebhook();
  // Block scheduling when the org has MLS entries still awaiting approval.
  // Mirror of the guard on the flow edit screen: orgs without mls_entries
  // (legacy / mid-onboarding) short-circuit to canScheduleFlows: true.
  const { canScheduleFlows, pendingMLS } = useMLSSubscriptionStatus(organization);

  const handleSchedule = async () => {
    // Defensive guard: the button is already disabled when MLS approval is
    // pending, but enforce it here too so the API call can't fire outside the
    // normal click path (re-enabled button, state race, future caller).
    if (!canScheduleFlows) return;
    try {
      setIsScheduling(true);
      await createFlowExecutions(flowId);

      if (flow.flow_type === 'Revive' || flow.flow_type === 'Engage') {
        sendReviveEngageWebhook(
          { ...flow, type: flow.flow_type } as unknown as FlowData,
          'scheduled'
        );
      }

      showToast({
        message: `Successfully scheduled flow executions for ${contactCount} contacts`,
        variant: 'success',
      });
      onClose();
    } catch (error) {
      showToast({
        message: `Error scheduling flow executions: ${
          error instanceof Error ? error.message : 'Unknown error occurred'
        }`,
        variant: 'error',
      });
    } finally {
      setIsScheduling(false);
    }
  };

  return (
    <>
      <LoadingDialog
        isOpen={isScheduling}
        message={`Scheduling Flow... Please wait while we schedule the flow for ${contactCount} contacts`}
      />
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <Modal.Header>
          <Typography variant="h3" className="font-bold">
            Schedule Flow
          </Typography>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-8">
            {!canScheduleFlows && (
              <Alert variant="warning">
                <AlertDescription>
                  <span className="font-medium">MLS approval pending</span> - you can create the
                  flow, but scheduling is locked until MLS access is approved for:{' '}
                  <span className="font-medium">{pendingMLS.join(', ')}</span>.
                </AlertDescription>
              </Alert>
            )}
            <div className="flex flex-col justify-center items-center gap-2 mb-5">
              <Typography variant="h5" className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Flow Summary
              </Typography>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Review your flow settings below before scheduling
              </p>
            </div>

            <div className="space-y-6">
              {/* Flow Information Card */}
              <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
                <div className="flex items-center gap-4 mb-6">
                  <IconPill icon={FileText} size="lg" />
                  <Typography variant="h6" className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Flow Information
                  </Typography>
                </div>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Flow Name</div>
                      <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">{flowName}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Flow Type</div>
                      <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">{flow.flow_type}</div>
                    </div>
                  </div>
                  <div className={`grid ${flow.sub_type ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'} gap-6`}>
                    {flow.sub_type && (
                      <div>
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Flow Source</div>
                        <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">
                          {capitalizeFirst(flow.sub_type)}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Transfer Line(s)</div>
                      <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">
                        {transferLines.length > 0 ? transferLines.join(', ') : 'None'}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6">
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Description</div>
                  <div className="mt-2 text-sm text-gray-700 dark:text-gray-400 whitespace-pre-wrap rounded">
                    {flow?.description || 'No description provided'}
                  </div>
                </div>
              </div>

              {/* Schedule Settings Card */}
              <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
                <div className="flex items-center gap-4 mb-6">
                  <IconPill icon={Calendar} size="lg" />
                  <Typography variant="h6" className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Schedule Settings
                  </Typography>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Scheduled Date/Time</div>
                    <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">
                      {flow?.scheduled_for
                        ? formatDate(flow.scheduled_for, timezone)
                        : 'Not scheduled'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Scheduling Window</div>
                    <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">
                      {flow ? `${flow.scheduling_window} ${flow.schedule_over_unit}` : 'Not set'}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Allowed Hours</div>
                    <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">
                      {flow?.allowed_hours
                        ? `${flow.allowed_hours.start_hour}:00 - ${flow.allowed_hours.end_hour}:00`
                        : 'Not set'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Allowed Days</div>
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-2">
                        {(flow?.allowed_days || []).map((day: string) => (
                          <span
                            key={day}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                          >
                            {day}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Settings Card */}
              <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
                <div className="flex items-center gap-4 mb-6">
                  <IconPill icon={MapPin} size="lg" />
                  <Typography variant="h6" className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Location Settings
                  </Typography>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Timezone</div>
                    <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">{timezone}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Country</div>
                    <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">United States</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Exclude Holidays</div>
                    <div className="mt-2">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          flow?.exclude_holidays
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-gray-200 text-gray-800 dark:bg-white/10 dark:text-gray-300'
                        }`}
                      >
                        {flow?.exclude_holidays ? (
                          <Check className="w-3 h-3" />
                        ) : (
                          <X className="w-3 h-3" />
                        )}
                        {flow?.exclude_holidays ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information Card */}
              <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
                <div className="flex items-center gap-4 mb-6">
                  <IconPill icon={Users} size="lg" />
                  <Typography variant="h6" className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Contact Information
                  </Typography>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Contacts added</div>
                    <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">{contactCount}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Selected Tags</div>
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-2">
                        {selectedTags.map((tag) => (
                          <Badge
                            key={tag}
                            variant="secondary"
                            className="flex items-center bg-[#ffe5bc] text-[#88612a] dark:bg-amber-900/30 dark:text-amber-300 gap-1"
                          >
                            <Tag className="w-3 h-3" />
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-2 w-full">
            <Button variant="outline" onClick={onClose} disabled={isScheduling}>
              Close
            </Button>
            <Button
              onClick={handleSchedule}
              disabled={isScheduling || !canScheduleFlows}
              isLoading={isScheduling}
              title={
                !canScheduleFlows ? `MLS approval pending: ${pendingMLS.join(', ')}` : undefined
              }
            >
              Schedule Now
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};
